import { useRef, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Loader2 } from 'lucide-react';
import { ChatPlusIn, EditPencil } from 'iconoir-react';
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from 'components/ui/accordion';
import { useGetAgentConversationList } from '../hooks/use-agent-conversation';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { resolveConversationAgentId } from '../utils/is-agent-chat-conversation';
import { DEFAULT_AGENT_CHAT_TITLE } from 'utils/constants/chat-constants';
import { CreateAgentIcon } from 'components/blocks/layout/icons/create-agent-icon';
import { cn } from 'lib/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AgentSummary } from '../agent-chat-module/types/agent.service.type';
import { useSidebar } from 'components/ui/sidebar';
import { useConfig } from 'hooks/use-config';
import { SidebarTreeItem } from 'components/blocks/layout/sidebar-items';
import {
  SIDEBAR_NAV_ICON_SIZE,
  SIDEBAR_NAV_ICON_STROKE,
  sidebarNavIconClass,
} from 'components/blocks/layout/sidebar-nav-icon';

interface AgentChatAccordionProps {
  agent: AgentSummary;
  chatId?: string;
  variant?: 'default' | 'figma';
  isOpen?: boolean;
  isMobile?: boolean;
  setOpenMobile?: (open: boolean) => void;
  onActiveChatFound?: (agentId: string) => void;
  renderRightAction?: (id: string, title: string) => ReactNode;
}

export const AgentChatAccordion = ({
  agent,
  chatId,
  variant = 'default',
  isOpen: isOpenControlled,
  isMobile: isMobileProp,
  setOpenMobile: setOpenMobileProp,
  onActiveChatFound,
  renderRightAction,
}: AgentChatAccordionProps) => {
  const agentId = agent.id;
  const agentName = agent.name || 'Unnamed Agent';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sidebar = useSidebar();
  const isMobile = isMobileProp ?? sidebar.isMobile;
  const setOpenMobile = setOpenMobileProp ?? sidebar.setOpenMobile;
  const { openMobile } = sidebar;
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';
  const { t } = useTranslation();

  const activeAgentIdFromUrl = searchParams.get('agent') || searchParams.get('agentId');
  const sidebarConversationsList = useChatStore((state) => state.sidebarConversationsList);
  const storeConversationAgentId = useChatStore((state) =>
    chatId ? state.conversations[chatId]?.agentId : undefined,
  );
  const activeConversationAgentId = resolveConversationAgentId({
    conversationId: chatId,
    agentIdFromUrl: activeAgentIdFromUrl,
    storeConversation: storeConversationAgentId ? { agentId: storeConversationAgentId } : undefined,
    sidebarConversationsList,
  });

  const sidebarHasActiveChat = Boolean(
    chatId &&
    (sidebarConversationsList.some(
      (item) => item.ConversationId === chatId && item.AgentId === agentId,
    ) ||
      activeConversationAgentId === agentId),
  );

  // Only fetch this agent's conversations when its accordion is expanded — otherwise
  // rendering the Agents list would fire one /collaboration/conversations per agent at once.
  const [figmaOpen, setFigmaOpen] = useState<boolean>(
    activeAgentIdFromUrl === agentId ||
      activeConversationAgentId === agentId ||
      sidebarHasActiveChat,
  );
  useEffect(() => {
    if (activeAgentIdFromUrl === agentId || activeConversationAgentId === agentId) {
      setFigmaOpen(true);
    }
  }, [activeAgentIdFromUrl, activeConversationAgentId, agentId]);

  useEffect(() => {
    if (!sidebarHasActiveChat) return;
    setFigmaOpen(true);
    onActiveChatFound?.(agentId);
  }, [sidebarHasActiveChat, agentId, onActiveChatFound]);

  const isExpanded = variant === 'figma' ? figmaOpen : (isOpenControlled ?? false);

  const agentChatListContainerRef = useRef<HTMLDivElement>(null);
  const agentLoadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data: agentConversationsData,
    fetchNextPage: fetchNextAgentPage,
    hasNextPage: hasNextAgentPage,
    isFetchingNextPage: isFetchingNextAgentPage,
    isLoading: isLoadingAgentConversations,
  } = useGetAgentConversationList(
    {
      agent_id: agentId,
      project_key: projectKey,
    },
    { enabled: isExpanded },
  );

  const agentChatList = useMemo(() => {
    const seenIds = new Set<string>();
    const serverChats = (agentConversationsData?.pages ?? []).flatMap((page) => {
      const filtered = page.sessions.filter((session: any) => {
        const sessionId = session.session_id;
        if (!sessionId || seenIds.has(sessionId)) {
          return false;
        }
        seenIds.add(sessionId);
        return true;
      });

      return filtered.map((session: any) => {
        const sidebarItem = sidebarConversationsList.find(
          (it) => it.ConversationId === session.session_id,
        );
        const sidebarTitle = sidebarItem?.ConversationTitle?.trim();

        return {
          id: session.session_id,
          lastEntryDate: session.last_entry_date,
          title:
            sidebarTitle ||
            session.conversation?.Title ||
            DEFAULT_AGENT_CHAT_TITLE,
        };
      });
    });

    // Conversations created this session for this agent that the server list doesn't yet
    // have — show them under the agent immediately (deduped once the server list catches up).
    const optimistic = sidebarConversationsList
      .filter((it) => it.AgentId === agentId && it.ConversationId && !seenIds.has(it.ConversationId))
      .map((it) => ({
        id: it.ConversationId,
        lastEntryDate: it.LastUpdatedDate,
        title: it.ConversationTitle || DEFAULT_AGENT_CHAT_TITLE,
      }));

    return [...optimistic, ...serverChats];
  }, [agentConversationsData?.pages, sidebarConversationsList, agentId]);

  const hasActiveChat = Boolean(
    chatId &&
    (sidebarHasActiveChat || agentChatList.some((chat) => chat.id === chatId)),
  );
  const isAgentActive = activeConversationAgentId === agentId || hasActiveChat;

  const isOpen = isExpanded;

  useEffect(() => {
    if (!hasActiveChat) return;
    setFigmaOpen(true);
    onActiveChatFound?.(agentId);
  }, [hasActiveChat, agentId, onActiveChatFound]);

  const renderAgentChatItem = (chat: any) => (
    <SidebarTreeItem
      key={chat.id}
      label={chat.title}
      isActive={chatId === chat.id}
      depth={1}
      variant={variant}
      onClick={() => {
        navigate(`/chat/${chat.id}`);
        if (isMobile) {
          setOpenMobile(false);
        }
      }}
      rightAction={renderRightAction?.(chat.id, chat.title)}
    />
  );

  const handleNewAgentChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    navigate(`/chat?agentId=${encodeURIComponent(agentId)}`);

    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleConfigureAgent = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    navigate(`/agent-details/${encodeURIComponent(agentId)}/configuration`);

    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const agentRowActions = (
    <div className="flex items-center">
      <button
        type="button"
        className="rounded-md p-1 hover:bg-[hsl(var(--grey-100))] dark:hover:bg-sidebar-accent"
        onClick={handleConfigureAgent}
        title={`Configure ${agentName}`}
        aria-label={`Configure ${agentName}`}
      >
        <EditPencil
          width={14}
          height={14}
          strokeWidth={SIDEBAR_NAV_ICON_STROKE}
          className="text-[#3D3D3D] dark:text-foreground"
        />
      </button>
      <button
        type="button"
        className="rounded-md p-1 hover:bg-[hsl(var(--grey-100))] dark:hover:bg-sidebar-accent"
        onClick={handleNewAgentChat}
        title={`New chat with ${agentName}`}
        aria-label={`New chat with ${agentName}`}
      >
        <ChatPlusIn
          width={14}
          height={14}
          strokeWidth={SIDEBAR_NAV_ICON_STROKE}
          className="text-[#3D3D3D] dark:text-foreground"
        />
      </button>
    </div>
  );

  const conversationChildren =
    isLoadingAgentConversations && agentChatList.length === 0 ? (
      <div className="flex items-center justify-center py-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    ) : agentChatList.length === 0 ? (
      <div className="py-1 px-2 text-center">
        <p className="text-xs text-muted-foreground italic mb-2">
          {t('NO_CONVERSATIONS_YET') || 'No conversations yet'}
        </p>
      </div>
    ) : (
      <div
        ref={agentChatListContainerRef}
        className={cn(
          'flex flex-col gap-1',
          variant === 'figma' && 'mt-1',
          variant === 'default' && 'overflow-y-auto overflow-x-visible pr-1 space-y-1 mt-1'
        )}
        style={variant === 'default' ? { maxHeight: 'calc(100vh - 400px)' } : undefined}
      >
        {agentChatList.map((chat) => renderAgentChatItem(chat))}
        {hasNextAgentPage && <div ref={agentLoadMoreRef} className="h-10 w-full" />}
        {isFetchingNextAgentPage && (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isMobile && !openMobile) {
      return;
    }

    const setupObserver = () => {
      const currentLoadMoreRef = agentLoadMoreRef.current;
      const scrollContainer = agentChatListContainerRef.current;

      if (!currentLoadMoreRef || !scrollContainer) {
        return null;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && hasNextAgentPage && !isFetchingNextAgentPage) {
              fetchNextAgentPage();
            }
          });
        },
        {
          root: scrollContainer,
          rootMargin: '200px',
          threshold: 0.1,
        }
      );

      observer.observe(currentLoadMoreRef);
      return observer;
    };

    let observer = setupObserver();
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    if (!observer) {
      const delays = isMobile ? [100, 300, 500, 1000] : [100, 300];
      delays.forEach((delay) => {
        const timeout = setTimeout(() => {
          if (!observer) {
            observer = setupObserver();
          }
        }, delay);
        timeouts.push(timeout);
      });
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      timeouts.forEach(clearTimeout);
    };
  }, [
    hasNextAgentPage,
    isFetchingNextAgentPage,
    fetchNextAgentPage,
    agentChatList.length,
    isMobile,
    openMobile,
    isOpen,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isMobile && !openMobile) {
      return;
    }

    const setupScrollListener = () => {
      const scrollContainer = agentChatListContainerRef.current;

      if (!scrollContainer) {
        return null;
      }

      const handleScroll = () => {
        const scrollTop = scrollContainer.scrollTop;
        const scrollHeight = scrollContainer.scrollHeight;
        const clientHeight = scrollContainer.clientHeight;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        if (scrollPercentage > 0.75 && hasNextAgentPage && !isFetchingNextAgentPage) {
          fetchNextAgentPage();
        }
      };

      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    };

    let cleanup = setupScrollListener();
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    if (!cleanup) {
      const delays = isMobile ? [100, 300, 500, 1000] : [100, 300];
      delays.forEach((delay) => {
        const timeout = setTimeout(() => {
          if (!cleanup) {
            cleanup = setupScrollListener();
          }
        }, delay);
        timeouts.push(timeout);
      });
    }

    return () => {
      if (cleanup) {
        cleanup();
      }
      timeouts.forEach(clearTimeout);
    };
  }, [
    hasNextAgentPage,
    isFetchingNextAgentPage,
    fetchNextAgentPage,
    isMobile,
    openMobile,
    isOpen,
  ]);

  const [logoError, setLogoError] = useState(false);
  const logoUrl = agent.logo_url?.trim();
  const showLogo = Boolean(logoUrl) && !logoError;

  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  const agentIcon = showLogo ? (
    <img
      src={logoUrl!}
      alt={agentName}
      className="size-5 shrink-0 rounded-full object-cover"
      width={20}
      height={20}
      onError={() => setLogoError(true)}
    />
  ) : (
    <CreateAgentIcon
      width={SIDEBAR_NAV_ICON_SIZE}
      height={SIDEBAR_NAV_ICON_SIZE}
      strokeWidth={SIDEBAR_NAV_ICON_STROKE}
      className={sidebarNavIconClass}
    />
  );

  if (variant === 'figma') {
    return (
      <SidebarTreeItem
        label={agentName}
        iconElement={agentIcon}
        variant="figma"
        isActive={isAgentActive}
        defaultOpen={isAgentActive}
        open={figmaOpen}
        onOpenChange={setFigmaOpen}
        className="pr-14"
        rightAction={agentRowActions}
      >
        {conversationChildren}
      </SidebarTreeItem>
    );
  }

  return (
    <AccordionItem value={agentId} className="border-none">
      <AccordionTrigger
        data-active={isAgentActive}
        className={cn(
          'hover:no-underline rounded-lg h-8 py-0',
          'group/trigger hover:[&:not(:has(button:hover))]:bg-sidebar-accent hover:[&:not(:has(button:hover))]:text-sidebar-accent-foreground',
          !isAgentActive && '[&[data-state=open]]:bg-accent/30',
          isAgentActive &&
          'bg-sidebar-accent text-sidebar-accent-foreground font-medium [&[data-state=open]]:bg-sidebar-accent [&[data-state=open]]:text-sidebar-accent-foreground',
          'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-medium',
          '[&>svg]:hidden',
          'w-full min-w-0'
        )}
        style={{ paddingLeft: '40px', paddingRight: '8px' }}
      >
        <div className="flex items-center w-full min-w-0 gap-3">
          {showLogo ? (
            <img
              src={logoUrl!}
              alt={agentName}
              className="size-5 shrink-0 rounded-full  object-cover"
              width={20}
              height={20}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="h-4 w-4 flex-shrink-0 rounded-full border border-primary flex items-center justify-center bg-primary/10">
              <span className="text-[10px] font-medium text-primary uppercase">
                {agentName.charAt(0)}
              </span>
            </div>
          )}

          <span
            className="min-w-0 flex-1 truncate text-left text-sm font-normal leading-[1.2] text-[#3D3D3D] dark:text-foreground"
            title={agentName}
          >
            {agentName}
          </span>

          {agentRowActions}

          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 flex-shrink-0 group-data-[state=open]/trigger:rotate-90" />
        </div>
      </AccordionTrigger>

      <AccordionContent className="pb-0 pt-0">{conversationChildren}</AccordionContent>
    </AccordionItem>
  );
};